/**
 * LazyForeignKeyCell
 *
 * Renders an FK cell in Query Editor results. On click, lazily resolves the
 * FK constraint (fetches table metadata), then opens ForeignKeyPopover.
 * Constraint metadata is cached per (connectionId, database, schema, table).
 */

import { useState, useCallback, useMemo } from 'react';
import { ExternalLink, Link2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { ForeignKeyPopover } from '../../table-explorer/components/ForeignKeyPopover';
import { resolveFkTarget } from '../../common/utils/fkConstraintResolver';
import type { ColumnForeignKeyInfo } from '../../table-explorer/hooks/useTableMetadata';
import type { ConstraintMetadata } from '../../../../types/electron';
import type { DatabaseEngineId } from '../../../../core/engines';
import { toast } from '../../../../components/ui/toaster';
import { useMetaInfo } from '../../common/hooks/useMetaInfo';
import { extractJsonPathScalar } from '../../common/utils/extractJsonPath';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { evaluateFilterGroupsAgainstRow } from '../../common/utils/filterEvaluator';
import { openTableExplorerTab } from '../../table-explorer/utils/openTableExplorerTab';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { getEngineForConnection } from '../../../connections/utils/connectionLookup';
type LazyForeignKeyCellProps = {
  connectionId: string;
  database: string;
  sourceSchema: string;
  sourceTable: string;
  sourceColumn: string;
  value: unknown;
  displayValue: string;
  /**
   * When true, the FK is user-declared via MetaInfo (loose). When undefined,
   * we look it up from MetaInfo here. This makes the prop optional for callers
   * (Query Editor) whose column shape doesn't propagate `isLoose`.
   */
  isLoose?: boolean;
  /** Surface where the cell is rendered — propagated to the popover-opened analytics event. */
  surface?: 'query_editor' | 'table_explorer';
  /** Full source row data — threaded from the grid for use by filter-aware loose FK logic (Task 3.5). */
  sourceRow?: Record<string, unknown>;
};

type FkCacheEntry = {
  constraints: ConstraintMetadata[];
  /** The source table's column names — `resolveFkTarget` reads the source column by its exact name. */
  columnNames?: string[];
};

export function LazyForeignKeyCell({
  connectionId,
  database,
  sourceSchema,
  sourceTable,
  sourceColumn,
  value,
  displayValue,
  isLoose: isLooseProp,
  surface,
  sourceRow,
}: LazyForeignKeyCellProps) {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();
  const [isResolving, setIsResolving] = useState(false);
  const [resolvedFk, setResolvedFk] = useState<ColumnForeignKeyInfo | null>(null);
  const [showPopover, setShowPopover] = useState(false);

  // When the caller didn't declare loose-ness, infer it from MetaInfo.
  // This covers Query Editor (whose QueryColumn shape doesn't carry isLoose).
  const { data: metaInfo } = useMetaInfo(connectionId, database);

  // Multi-match loose FK lookup gated by source-row filter evaluation.
  // If sourceRow is not available (rare), skip filter gating and return all
  // matches. A single source column can host multiple loose FK declarations
  // (polymorphic associations distinguished by sourceFilter, or JSON-pathed
  // FKs distinguished by sourcePath).
  const matchingLooseRels = useMemo(() => {
    if (!sourceSchema || !sourceTable || !sourceColumn) return [];
    const all = (metaInfo?.looseRelationships ?? []).filter(
      (r) =>
        r.fromSchema === sourceSchema &&
        r.fromTable === sourceTable &&
        r.fromColumn === sourceColumn,
    );
    if (!sourceRow) return all; // no row in scope → don't gate
    return all.filter((r) => evaluateFilterGroupsAgainstRow(sourceRow, r.sourceFilter));
  }, [metaInfo, sourceSchema, sourceTable, sourceColumn, sourceRow]);

  const looseRel = matchingLooseRels[0] ?? null;
  const isLoose = isLooseProp ?? matchingLooseRels.length > 0;

  // For JSON-pathed loose FKs the cell holds a JSON document; the lookup key
  // lives at `sourcePath` inside it. Extract client-side so the popover and
  // the "Open in Table Explorer" path filter on the same scalar that the SQL
  // builder would emit (see `extractTextSql` in `jsonFilterSql.ts`).
  const navigationValue =
    looseRel?.sourcePath != null
      ? extractJsonPathScalar(value, looseRel.sourcePath) ?? null
      : value;

  const cacheKey = ['table-constraints', connectionId, database, sourceSchema, sourceTable];

  const handleClick = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    // Note: `feature:fk_cell_popover_opened` is fired by ForeignKeyPopover's
    // onOpenChange so both query-editor and table-explorer cell triggers count.

    const engineId: DatabaseEngineId =
      (getEngineForConnection(connectionId) as DatabaseEngineId) || 'mssql';

    // FK Breadcrumb (Slice 3) — ⌘-click / Ctrl-click bypass: open the FK
    // target in a new TE tab directly, no popover.
    const isModifier = e.metaKey || e.ctrlKey;
    if (isModifier) {
      // Best-effort: derive the FK target like the popover would. For loose
      // FKs we already know the target; for real FKs we need the metadata.
      if (isLoose && looseRel) {
        openTableExplorerTab({
          connectionId,
          database,
          schema: looseRel.toSchema,
          tableName: looseRel.toTable,
          filters: [
            { field: looseRel.toColumn, conditions: [{ operator: '=', value: navigationValue as string | number | boolean | Date | null }] },
          ],
        });
        track('feature:fk_nav_open_new_explorer', {
          source: 'modifier-click-fk',
          targetKind: 'row',
          engine: engineId,
        });
        return;
      }
      // Real FK — resolve constraint then open new tab without showing popover.
      setIsResolving(true);
      try {
        let cached = queryClient.getQueryData<FkCacheEntry>(cacheKey);
        if (!cached) {
          const result = await window.electronAPI?.getTableMetadata({
            connectionId,
            database,
            schema: sourceSchema,
            tableName: sourceTable,
          });
          if (!result?.success || !result.metadata) {
            toast.error({
              title: 'Could not resolve FK target',
              description: result?.error || 'Failed to fetch table metadata',
            });
            return;
          }
          cached = {
            constraints: result.metadata.constraints || [],
            columnNames: (result.metadata.columns ?? []).map((c) => c.name),
          };
          queryClient.setQueryData(cacheKey, cached);
        }
        const fkInfo = resolveFkTarget(cached.constraints, sourceColumn, matchingLooseRels, cached.columnNames);
        if (!fkInfo) {
          toast.error({
            title: 'Could not resolve FK target',
            description: `No FK constraint found for column "${sourceColumn}"`,
          });
          return;
        }
        openTableExplorerTab({
          connectionId,
          database,
          schema: fkInfo.referencedSchema || '',
          tableName: fkInfo.referencedTable,
          filters: [
            { field: fkInfo.referencedColumn, conditions: [{ operator: '=', value: navigationValue as string | number | boolean | Date | null }] },
          ],
        });
        track('feature:fk_nav_open_new_explorer', {
          source: 'modifier-click-fk',
          targetKind: 'row',
          engine: engineId,
        });
      } finally {
        setIsResolving(false);
      }
      return;
    }

    // If already resolved, just open the popover
    if (resolvedFk) {
      setShowPopover(true);
      return;
    }

    // Loose FKs aren't in the DB constraint table — resolve from MetaInfo directly.
    if (isLoose) {
      if (!looseRel) {
        toast.error({
          title: 'Could not resolve loose FK target',
          description: `No declared loose relationship for "${sourceColumn}"`,
        });
        return;
      }
      setResolvedFk({
        constraintName: `loose:${looseRel.id}`,
        referencedSchema: looseRel.toSchema,
        referencedTable: looseRel.toTable,
        referencedColumn: looseRel.toColumn,
        isLoose: true,
      });
      setShowPopover(true);
      return;
    }

    setIsResolving(true);

    try {
      // Check TanStack Query cache first
      let cached = queryClient.getQueryData<FkCacheEntry>(cacheKey);

      if (!cached) {
        // Fetch table metadata to get constraints
        const result = await window.electronAPI?.getTableMetadata({
          connectionId,
          database,
          schema: sourceSchema,
          tableName: sourceTable,
        });

        if (!result?.success || !result.metadata) {
          toast.error({
            title: 'Could not resolve FK target',
            description: result?.error || 'Failed to fetch table metadata',
          });
          setIsResolving(false);
          return;
        }

        cached = {
          constraints: result.metadata.constraints || [],
          columnNames: (result.metadata.columns ?? []).map((c) => c.name),
        };
        queryClient.setQueryData(cacheKey, cached);
      }

      const fkInfo = resolveFkTarget(cached.constraints, sourceColumn, matchingLooseRels, cached.columnNames);

      if (!fkInfo) {
        toast.error({
          title: 'Could not resolve FK target',
          description: `No FK constraint found for column "${sourceColumn}"`,
        });
        setIsResolving(false);
        return;
      }

      setResolvedFk(fkInfo);
      setShowPopover(true);
    } catch (err) {
      toast.error({
        title: 'Could not resolve FK target',
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setIsResolving(false);
    }
  }, [resolvedFk, queryClient, cacheKey, connectionId, database, sourceSchema, sourceTable, sourceColumn, isLoose, looseRel, matchingLooseRels, navigationValue, track]);

  // Once FK is resolved, render the full popover-wrapped cell
  if (resolvedFk && showPopover) {
    return (
      <ForeignKeyPopover
        connectionId={connectionId}
        database={database}
        referencedSchema={resolvedFk.referencedSchema || ''}
        referencedTable={resolvedFk.referencedTable}
        referencedColumn={resolvedFk.referencedColumn}
        value={navigationValue}
        defaultOpen
        // A composite-FK member resolves to its loose declaration
        // (`resolveFkTarget` precedence) — the popover then reads it as loose.
        isLoose={isLoose || !!resolvedFk.isLoose}
        sourceSchema={sourceSchema}
        sourceTable={sourceTable}
        sourceColumn={sourceColumn}
        surface={surface}
        sourceRow={sourceRow}
        matchingLooseRels={matchingLooseRels}
      >
        {displayValue}
      </ForeignKeyPopover>
    );
  }

  // Default: clickable FK cell with distinct styling for loose vs real FK.
  // - Real FK: solid blue underline + ExternalLink icon (unchanged).
  // - Loose FK: dashed teal underline + Link2 icon, with a "loose" tooltip.
  const buttonClass = isLoose
    ? 'inline-flex items-center gap-1 min-w-0 text-teal-600 dark:text-teal-400 hover:text-teal-500 underline decoration-dashed decoration-teal-500/60 underline-offset-2 cursor-pointer'
    : 'inline-flex items-center gap-1 min-w-0 text-blue-600 dark:text-blue-400 hover:underline cursor-pointer';
  const tooltip = isLoose
    ? 'Loose relationship — click to view related row'
    : `View related row (FK from ${sourceSchema}.${sourceTable})`;
  const Icon = isLoose ? Link2 : ExternalLink;

  return (
    <button
      type="button"
      className={buttonClass}
      onClick={handleClick}
      title={tooltip}
      data-testid="fk-cell-trigger"
      data-loose={isLoose ? 'true' : 'false'}
    >
      <span className="truncate">{displayValue}</span>
      {isResolving ? (
        <RandomDotmSpinner size={12} className="flex-shrink-0" />
      ) : (
        <Icon className="h-3 w-3 opacity-60 flex-shrink-0" />
      )}
    </button>
  );
}
